/**
 * Authentication utilities for the Kanopi authentication system.
 *
 * This file contains utilities for:
 * - JWT token retrieval and management
 * - Authentication redirect flow
 * - Error handling for authentication failures
 * - CSRF token generation and validation
 * - State management for authentication callbacks
 *
 * @module authUtils
 */

import {
  KanopiAuthErrorType,
  KanopiAuthErrorMessages,
} from "../types/kanopiAuthErrors";
import { handleErrorDisplay } from "kw/util/utils";
import { KANOPI_AUTHENTICATOR } from "kw/auth/constants/auth";
import settings from "kw/settings";

/**
 * Retrieves JWT token from cookies
 *
 * @returns {string|undefined} JWT token if found in cookies, undefined otherwise
 */
export const getJwtFromCookies = (): string | undefined => {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${KANOPI_AUTHENTICATOR.JWT_COOKIE_NAME}=`))
    ?.split("=")[1];
};

/**
 * Clears authentication cookies by setting their expiration to immediate
 */
export const clearAuthCookies = (): void => {
  document.cookie = `${KANOPI_AUTHENTICATOR.JWT_COOKIE_NAME}=; path=/; max-age=0`;
  document.cookie = `${KANOPI_AUTHENTICATOR.SESSION_COOKIE_NAME}=; path=/; max-age=0`;
};

/**
 * Generates a cryptographically secure random token for CSRF protection
 *
 * @returns {string} Hex-encoded random token
 */
export const generateStateToken = (): string => {
  return Array.from(window.crypto.getRandomValues(new Uint8Array(32)))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

/**
 * Initiates the authentication redirect flow
 * Generates a CSRF state token, stores it in sessionStorage,
 * and redirects the user to the authentication portal
 */
export const startAuthRedirect = (orgSlug: string | null): void => {
  // Generate state token for CSRF protection
  const stateToken = generateStateToken();

  // Store state in sessionStorage for verification during callback
  sessionStorage.setItem("csrf_state", stateToken);

  // Use the environment-specific auth URL from settings
  const baseUrl = settings.KANOPI_AUTH_URL;

  // Build redirect URL to the authentication portal
  const callbackUrl = `${window.location.origin}${window.location.pathname}?step=callback`;
  let redirectUrl = `${baseUrl}/login`;
  if (orgSlug) redirectUrl += `/${orgSlug.toLowerCase()}`;
  redirectUrl += `?redirect=${encodeURIComponent(callbackUrl)}&state=${stateToken}`;

  // Redirect to authentication portal
  window.location.href = redirectUrl;
};

/**
 * Processes authentication errors by displaying appropriate error messages
 *
 * @param {any} error - Error object from failed authentication
 */
export const handleAuthError = (error: any): void => {
  // Check if the error message contains a specific error code
  // and display the corresponding error message
  // If no specific error code is found, display a generic error message
  const errorMessage = error.message || "";
  const errorCode = Object.values(KanopiAuthErrorType).find((code) =>
    errorMessage.includes(code)
  );

  if (errorCode) {
    const errorMessageKey = errorCode as keyof typeof KanopiAuthErrorMessages;
    // Check if we have a corresponding message for this error code
    if (KanopiAuthErrorMessages[errorMessageKey]) {
      handleErrorDisplay(
        { message: errorCode },
        KanopiAuthErrorMessages[errorMessageKey]
      );
      return;
    }
  }

  // Generic fallback error message
  handleErrorDisplay({ message: "Authentication failed" });
};

/**
 * Manages callback state to prevent duplicate processing and ensure security
 */
export const callbackStateManager = {
  /**
   * Checks if a callback with the given state has already been processed
   *
   * @param {string} state - State parameter from the callback URL
   * @returns {boolean} True if the callback has already been processed
   */
  isProcessed: (state: string): boolean => {
    const key = `callback_processed_${state}`;
    return !!sessionStorage.getItem(key);
  },

  /**
   * Marks a callback with the given state as processed
   *
   * @param {string} state - State parameter from the callback URL
   */
  markAsProcessed: (state: string): void => {
    const key = `callback_processed_${state}`;
    sessionStorage.setItem(key, "true");
  },

  /**
   * Validates the state parameter against the stored state
   *
   * @param {string} state - State parameter from the callback URL
   * @returns {boolean} True if the state is valid
   */
  validateState: (state: string): boolean => {
    const storedState = sessionStorage.getItem("csrf_state");
    return state === storedState;
  },
};
